import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Dispatch,
  Fragment,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { CSVCountCheckOrder, CSVThresholdCheckStock, CSVUploadCountResponseApi } from "../types";
import TableCellSmall, {
  MultiTypography,
  TableCellWide,
  TableHeaderCellSmall,
} from "./TableCellSmall";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 33,
  },
};

type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  onClose: Function;
  stockList: (CSVCountCheckOrder | CSVThresholdCheckStock)[];
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  isLoading?: boolean;
  error?: string;
  errorTitle?: string;
  setInit?: Dispatch<SetStateAction<boolean>>;
};
/**
 * 在庫数量更新確認モーダルのテーブル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function CSVConfirmTable({
  open,
  title,
  content,
  ActionButtons,
  onClose,
  stockList,
  isFastLoading,
  getMoreStockList,
  isLoading,
  error,
  errorTitle,
  setInit,
}: Props) {
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);
  const { showAlert } = useContext(SuccessAlertContext);

  // スクロール処理
  const handleScroll = async () => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (!isFastLoading || isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight + 10 >= scrollHeight) {
      // eslint-disable-next-line no-param-reassign
      isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    const node = contentRef.current;
    if (node) {
      node.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (node) {
        node.removeEventListener("scroll", handleScroll);
      }
    };
  }, [contentRef.current]);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [fireMoreLoadEvent]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setInit) {
      setInit(true);
      // if (error) showAlert(error, "error");
      if (error) {
        Notify?.setNotification?.({
          show: true,
          title: "CSVアップロードでエラーがあります。",
          message: error,
        });
      }
    }
  }, [open]);

  // 表示数がlimitの時、中のコンテンツが小さいと無限ローディングさせることができないため、少しスクロールできるようにする。
  useEffect(() => {
    if (stockList.length === limit && !isLoading) {
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    } else {
      setAdjustment(undefined);
    }
  }, [stockList, isLoading]);

  return (
    <Dialog open={open} onClose={() => onClose()} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {stockList.length !== 0 &&
                <TableHead>
                  <TableRow>
                    <TableHeaderCellSmall>商品コード</TableHeaderCellSmall>
                    <TableHeaderCellSmall>JANコード</TableHeaderCellSmall>
                    <TableHeaderCellSmall>大分類</TableHeaderCellSmall>
                    <TableHeaderCellSmall>仕入先名</TableHeaderCellSmall>
                    <TableHeaderCellSmall>ブランド</TableHeaderCellSmall>
                    <TableHeaderCellSmall>商品名</TableHeaderCellSmall>
                    <TableHeaderCellSmall>サイズ</TableHeaderCellSmall>
                    <TableHeaderCellSmall>ジョイフル仕切単価</TableHeaderCellSmall>
                    <TableHeaderCellSmall>削除</TableHeaderCellSmall>
                    {error && <TableHeaderCellSmall>エラー内容</TableHeaderCellSmall>}
                  </TableRow>
                </TableHead>
              }

              <TableBody>
                { stockList.map((s) => {
                  // eslint-disable-next-line no-param-reassign
                  s = s as CSVCountCheckOrder;
                  return (
                    <TableRow
                      key={s.item_cd}
                      sx={error && s.errors && s.errors.length > 0 ? { backgroundColor: "rgb(255,170,170)" } : {}}
                    >
                      <TableCellSmall multi>{s.item_cd}</TableCellSmall>
                      <TableCellSmall multi>{s.jan_cd}</TableCellSmall>
                      <TableCellSmall multi>{s.class_name}</TableCellSmall>
                      <TableCellSmall multi>{s.vendor_name}</TableCellSmall>
                      <TableCellSmall multi>{s.brand}</TableCellSmall>
                      <TableCellSmall multi>{s.item_name}</TableCellSmall>
                      <TableCellSmall multi>{s.size}</TableCellSmall>
                      <TableCellSmall multi>{s.store_wholesale_price}</TableCellSmall>
                      <TableCellSmall multi>
                        <Checkbox color="secondary" disabled />
                      </TableCellSmall>
                      {error &&
                        (s.errors && s.errors.length > 0 ? (
                          <TableCellWide multi>
                            <>
                              {
                                // eslint-disable-next-line react/no-array-index-key
                                s.errors.map((e,i) => (i === 0 ? e : <Fragment key={i}><br/>{e}</Fragment>))
                              }
                            </>
                          </TableCellWide>
                        ) : (
                          <TableCellWide />
                        ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {adjustment && (
            // 画面が大きいと無限スクロールが発火しないことがある
            <Box height={adjustment} />
          )}
          {isLoading && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>{ActionButtons}</DialogActions>
    </Dialog>
  );
}
